import { Component, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import {
    AccountProfileService, DealershipService, FirebaseFunctionsService, UsersService, UxHelperService
} from '@shared/services';
import { ViewBasePage } from '@shared/pages';
import type { TAccountProfile, TDealership, TUser } from '@shared/models';

@Component({
    'selector': 'app-users',
    'templateUrl': './users.page.html',
    'styleUrls': ['./users.page.scss'],
    'standalone': true,
    'imports': [IonicModule, CommonModule, FormsModule, RouterModule]
})
export class UsersPage extends ViewBasePage implements OnInit, OnDestroy {
    public dealership: TDealership;
    public accountProfile: TAccountProfile;
    private usersSubscription: any;
    public users: any = [];
    public selectedUser: any = null;
    public debugMode = false;

    constructor(private activatedRoute: ActivatedRoute, private accountProfileService: AccountProfileService,
        private usersService: UsersService,
        private dealershipService: DealershipService, injector: Injector) {
        super(injector);
    }

    public override async ngOnInit() {
        this.debugMode = this.sessionService.isDebugMode();
        this.dealership = this.dealershipService.getNullable();
        this.accountProfile = this.accountProfileService.getNullable();

        const uid = this.activatedRoute.snapshot.paramMap.get('uid');
        const accountNumber = uid.replace(/'/g, '');
        if (!accountNumber) {
            return;
        }
        this.dealership = await this.dealershipService.getDealershipByAccountNumber(accountNumber);
        // this.accountProfile = await this.accountProfileService.getProfileByAccountNumber(accountNumber);
        this.accountProfile = await this.accountProfileService.getAccountProfileByAccountNumber(accountNumber);

        const observable = this.usersService.getObservable();
        this.usersSubscription = observable.subscribe((users: TUser[]) => {
            this.users = users;
        });
    }

    public override ngOnDestroy() {
        this.usersSubscription.unsubscribe();
    }

    public onUserClick(user: any): void {
        this.selectedUser = user;
    }

    // http://localhost:8100/welcome?forcePromptForLoginCredentials=true&setEmail=abc&setPassword=123&userToken=xxx
    public async onLoginClick(domain: string): Promise<void> {
        const localhostUrl = 'localhost:8100/';
        const stagingUrl = 'https://staging.shuttlenomix.com/';
        const appUrl = 'https://app.shuttlenomix.com/';
        // const path         = (domain === 'app') ? appUrl : stagingUrl;
        let path = '';

        switch (domain) {
            case 'localhost':
                path = localhostUrl;
                break;

            case 'staging':
                path = stagingUrl;
                break;

            case 'app':
                path = appUrl;
                break;
        }

        try {
            const response = await this.firebaseFunctionsService.createCustomToken(this.selectedUser.uid);
            if (response.token) {
                const url = `${path}welcome?userToken=${response.token}`;
                // console.log(`url = ${url}`);
                navigator.clipboard.writeText(url);
                window.open(url, '_blank');
                const msg = `If a new tab didn't open automatically, then copy and paste this url into a new tab. url=${url}`;
                await this.uxHelperService.showAlert('Information', msg);
            }
        } catch (err) {
            await this.uxHelperService.showAlert('Error', JSON.stringify(err));
        }
    }

    public onAddUserClick(): void {
        this.router.navigateByUrl('/user');
    }

    public async onChatbotSmokeTestClick(): Promise<void> {
        try {
            const health = await this.firebaseFunctionsService.chatbotHealth();
            const ask = await this.firebaseFunctionsService.chatbotAsk('What is ShuttleNomix?', false);

            await this.uxHelperService.showAlert(
                'Chatbot Smoke Test',
                `Health: ${JSON.stringify(health)}\n\nAsk: ${JSON.stringify(ask)}`
            );
        } catch (err) {
            await this.uxHelperService.showAlert('Chatbot Smoke Test Failed', JSON.stringify(err));
        }
    }
}